import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Card flotante de ayuda para ítems de sidebar (desktop).
 *
 * Aparece a la derecha del ítem tras un hover SOSTENIDO (300ms): nombre +
 * descripción sobre fondo brand black. Compartido entre la sidebar clásica
 * (`components/Sidebar`) y la del shell nuevo (`pages/estilos/EstiloMinimal`)
 * para que las dos digan lo mismo con la misma cara
 * (ADR-2026-09-01-card-ayuda-hover-sidebar y su correctivo
 * ADR-2026-09-01-card-ayuda-sidebar-v2).
 *
 * Va portaleado a `document.body` con `position: fixed` calculada del rect del
 * ítem: las dos navs tienen `overflow-y-auto` (recorta el overflow horizontal,
 * un absolute adentro quedaría cortado) y el panel de contenido de desktop es
 * `fixed` (la trampa de stacking de siempre). `pointer-events-none`: es solo
 * informativo, nunca le roba el mouse al ítem.
 */
type CardAyuda = {
  top: number;
  left: number;
  title: string;
  text: string;
  /** Línea extra opcional, más apagada (ej. "Incluido en los planes pagos"). */
  nota?: string;
};

export function useCardAyudaSidebar() {
  const [card, setCard] = useState<CardAyuda | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const abrirCardAyuda = useCallback(
    (el: HTMLElement, title: string, text: string, nota?: string) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        // El ítem pudo desmontarse durante los 300ms (un popover que se cerró,
        // una lista que se re-filtró): el rect de un nodo desconectado es todo
        // ceros y el card aparecería pegado al borde superior izquierdo.
        if (!el.isConnected) return;
        const rect = el.getBoundingClientRect();
        setCard({
          top: rect.top + rect.height / 2,
          left: rect.right + 14,
          title,
          text,
          nota,
        });
      }, 300);
    },
    [],
  );

  const cerrarCardAyuda = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setCard(null);
  }, []);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // Cerrar al scrollear: la posición se calcula UNA vez del rect del ítem; si
  // la nav scrollea bajo el cursor el ítem se mueve sin mouseleave (sin
  // mousemove no hay evento) y el card quedaría flotando suelto. El scroll no
  // burbujea pero sí baja en captura: window + capture ve también el scroller
  // interno de la nav y el panel de contenido de desktop.
  useEffect(() => {
    if (!card) return;
    const cerrar = () => setCard(null);
    window.addEventListener("scroll", cerrar, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", cerrar, { capture: true });
  }, [card]);

  const cardAyuda = card
    ? createPortal(
        <div
          className="fixed z-[100000] pointer-events-none"
          style={{
            top: card.top,
            left: card.left,
            transform: "translateY(-50%)",
          }}
          role="tooltip"
        >
          <div
            className="relative w-[248px] rounded-xl bg-brand-black px-3.5 py-3 shadow-[0_12px_32px_rgb(var(--brand-black-rgb)_/_0.28),0_2px_8px_rgb(var(--brand-black-rgb)_/_0.18)]"
            style={{ animation: "sidebar-hover-card-in 160ms ease-out both" }}
          >
            {/* Caret: cuadradito rotado asomando por el borde izquierdo,
                centrado a la altura del ítem. */}
            <div
              className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-brand-black rotate-45 rounded-[1px]"
              aria-hidden="true"
            />
            {/* 13.5px = el tamaño de las etiquetas de los ítems de la sidebar
                del shell nuevo; 12/11 son los cuerpos chicos que ya usa la UI. */}
            <p className="text-[13.5px] font-semibold text-white leading-tight">
              {card.title}
            </p>
            <p className="mt-1 text-[12px] leading-[1.5] text-white/60">
              {card.text}
            </p>
            {card.nota && (
              <p className="mt-1.5 text-[11px] leading-[1.4] text-white/40">
                {card.nota}
              </p>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return { abrirCardAyuda, cerrarCardAyuda, cardAyuda };
}
